import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Search, Eye, Phone, Mail, MapPin, Calendar, Clock, Filter, Trash2, CheckCircle, XCircle, FileText, Truck, Building, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
// Optional override for the customer-facing site URL used in shared links.
// Falls back to whatever the admin is currently browsing on.
const PUBLIC_SITE_URL = process.env.REACT_APP_PUBLIC_SITE_URL || (typeof window !== 'undefined' ? window.location.origin : '');

export const BookingManagement = () => {
  const [bookings, setBookings] = useState([]);
  const [filteredBookings, setFilteredBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [linkedDocuments, setLinkedDocuments] = useState(null);

  useEffect(() => {
    fetchBookings();
  }, []);

  useEffect(() => {
    filterBookings();
  }, [searchTerm, statusFilter, bookings]);

  const fetchBookings = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/bookings/`);
      setBookings(response.data);
      setFilteredBookings(response.data);
    } catch (error) {
      toast.error('Failed to load bookings');
      console.error('Error fetching bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterBookings = () => {
    let filtered = [...bookings];

    // Filter by status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(booking => booking.status === statusFilter);
    }

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(booking =>
        (booking.company_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (booking.customer_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (booking.customer_phone || '').includes(searchTerm) ||
        (booking.customer_email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (booking.transport_type || '').toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredBookings(filtered);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'confirmed':
        return 'bg-green-100 text-green-800 border-green-300';
      case 'completed':
        return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'cancelled':
        return 'bg-red-100 text-red-800 border-red-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const formatTransportType = (type) => {
    return type.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-SG', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const viewDetails = (booking) => {
    openBookingDetails(booking);
  };

  const updateBookingStatus = async (bookingId, newStatus) => {
    try {
      const response = await axios.patch(
        `${BACKEND_URL}/api/bookings/${bookingId}/status`,
        { status: newStatus }
      );
      
      if (response.data.success) {
        toast.success(response.data.message);
        fetchBookings(); // Refresh the list
        // If we just confirmed, show the linked documents
        if (newStatus === 'confirmed' && response.data.delivery_order) {
          toast.success(`Auto-created: ${response.data.delivery_order} & ${response.data.invoice}`, {
            duration: 5000
          });
        }
      }
    } catch (error) {
      toast.error('Failed to update booking status');
    }
  };

  const fetchLinkedDocuments = async (bookingId) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/bookings/${bookingId}/documents`);
      setLinkedDocuments(response.data);
    } catch (error) {
      console.error('Error fetching linked documents:', error);
      setLinkedDocuments(null);
    }
  };

  // One-click "Send to Driver via WhatsApp" — fetches the linked DO, ensures a
  // signing token exists, then opens WhatsApp pre-filled with delivery details + link.
  const sendDOToDriverWhatsApp = async (booking) => {
    try {
      // 1. Fetch the linked DO for this booking
      const docsRes = await axios.get(`${BACKEND_URL}/api/bookings/${booking.id}/documents`);
      const doDoc = docsRes.data?.delivery_order;
      if (!doDoc?.id) {
        toast.error('No Delivery Order found for this booking yet.');
        return;
      }

      // 2. Ensure a signing token exists (idempotent — returns existing if set)
      const tokenRes = await axios.post(
        `${BACKEND_URL}/api/public/delivery-orders/${doDoc.id}/generate-sign-token`
      );
      const token = tokenRes.data?.sign_token;
      if (!token) {
        toast.error('Failed to create signing link.');
        return;
      }
      const signUrl = `${PUBLIC_SITE_URL}/sign/do/${token}`;

      // 3. Build a nicely-formatted WhatsApp message
      const lines = [
        `*NJK Logistics — Delivery Order ${doDoc.document_number}*`,
        '',
        `*Customer:* ${booking.company_name || booking.customer_name}`,
        `*Contact:* ${booking.customer_name} · ${booking.customer_phone}`,
        '',
        `*Date / Time:* ${booking.date} ${booking.time}`,
        `*Pickup:* ${booking.pickup_address}`,
        `*Delivery To:* ${booking.unload_address || booking.delivery_address || '-'}`,
        '',
        `*Site Contact:* ${booking.site_contact} · ${booking.site_phone}`,
        `*Vehicle:* ${booking.transport_type}`,
      ];
      if (booking.additional_info) {
        lines.push('', `*Notes:* ${booking.additional_info}`);
      }
      lines.push('', '👉 *Tap the link below at delivery to capture both signatures (driver & customer):*', signUrl);

      const text = encodeURIComponent(lines.join('\n'));
      window.open(`https://wa.me/?text=${text}`, '_blank');
      toast.success('WhatsApp opened — pick your driver and send.');
    } catch (e) {
      console.error(e);
      toast.error('Failed to prepare driver WhatsApp message.');
    }
  };

  const openBookingDetails = (booking) => {
    setSelectedBooking(booking);
    setShowDetails(true);
    if (booking.status === 'confirmed' || booking.status === 'completed') {
      fetchLinkedDocuments(booking.id);
    } else {
      setLinkedDocuments(null);
    }
  };

  const deleteBooking = async (bookingId) => {
    if (!window.confirm('Are you sure you want to delete this booking? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await axios.delete(`${BACKEND_URL}/api/bookings/${bookingId}`);
      
      if (response.data.success) {
        toast.success(response.data.message);
        fetchBookings(); // Refresh the list
        if (showDetails) {
          setShowDetails(false);
        }
      }
    } catch (error) {
      toast.error('Failed to delete booking');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-40">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 py-6">
          <h1 className="text-3xl font-bold text-gray-900">Booking Management</h1>
          <p className="text-gray-600 mt-1">View and manage all customer bookings</p>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="container mx-auto px-4 py-6">
        <Card>
          <CardContent className="pt-6">
            <div className="grid md:grid-cols-2 gap-4">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <Input
                  placeholder="Search by name, phone, email, or transport type..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>

              {/* Status Filter */}
              <div className="flex items-center gap-2">
                <Filter size={20} className="text-gray-400" />
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Bookings</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="confirmed">Confirmed</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-2xl font-bold text-gray-900">{bookings.length}</p>
                <p className="text-sm text-gray-600">Total Bookings</p>
              </div>
              <div className="bg-yellow-50 p-4 rounded-lg">
                <p className="text-2xl font-bold text-yellow-800">
                  {bookings.filter(b => b.status === 'pending').length}
                </p>
                <p className="text-sm text-yellow-700">Pending</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg">
                <p className="text-2xl font-bold text-green-800">
                  {bookings.filter(b => b.status === 'confirmed').length}
                </p>
                <p className="text-sm text-green-700">Confirmed</p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-2xl font-bold text-blue-800">
                  {bookings.filter(b => b.status === 'completed').length}
                </p>
                <p className="text-sm text-blue-700">Completed</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Bookings List */}
      <div className="container mx-auto px-4 pb-8">
        {loading ? (
          <div className="text-center py-20">
            <div className="inline-block w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
            <p className="mt-4 text-gray-600">Loading bookings...</p>
          </div>
        ) : filteredBookings.length === 0 ? (
          <Card>
            <CardContent className="py-20 text-center">
              <p className="text-gray-500 text-lg">No bookings found</p>
              <p className="text-gray-400 mt-2">Try adjusting your search or filters</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredBookings.map((booking) => (
              <Card key={booking.id} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="grid md:grid-cols-12 gap-4 items-start">
                    {/* Booking Info - Left Side */}
                    <div className="md:col-span-8 space-y-3">
                      {/* Header Row */}
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="text-xl font-bold text-gray-900">{booking.company_name || booking.customer_name}</h3>
                          {booking.company_name && (
                            <p className="text-sm text-gray-600 mt-0.5">Contact: {booking.customer_name}</p>
                          )}
                          <p className="text-sm text-gray-500 mt-1">
                            Booking ID: {booking.id.substring(0, 8)}...
                          </p>
                        </div>
                        <Badge className={`${getStatusColor(booking.status)} border`}>
                          {booking.status.charAt(0).toUpperCase() + booking.status.slice(1)}
                        </Badge>
                      </div>

                      {/* Transport Type */}
                      <div className="bg-blue-50 inline-block px-3 py-1 rounded-md">
                        <span className="text-blue-800 font-medium text-sm">
                          {booking.vehicle_qty && parseInt(booking.vehicle_qty) > 1 ? `${booking.vehicle_qty}x ` : ''}{formatTransportType(booking.transport_type)}
                          {booking.rate_type === 'per_day' ? ' (1-Day)' : ' (Per Trip)'}
                        </span>
                      </div>
                      {booking.num_locations > 1 && (
                        <div className="bg-orange-50 inline-block px-3 py-1 rounded-md ml-2">
                          <span className="text-orange-800 font-medium text-sm">
                            {booking.num_locations} Destinations
                          </span>
                        </div>
                      )}

                      {/* Contact Info */}
                      <div className="grid sm:grid-cols-2 gap-2 text-sm">
                        <div className="flex items-center gap-2 text-gray-600">
                          <Phone size={16} />
                          <a href={`tel:${booking.customer_phone}`} className="hover:text-blue-600">
                            {booking.customer_phone}
                          </a>
                        </div>
                        <div className="flex items-center gap-2 text-gray-600">
                          <Mail size={16} />
                          <a href={`mailto:${booking.customer_email}`} className="hover:text-blue-600">
                            {booking.customer_email}
                          </a>
                        </div>
                      </div>

                      {/* Date and Time */}
                      <div className="flex flex-wrap gap-4 text-sm">
                        <div className="flex items-center gap-2 text-gray-600">
                          <Calendar size={16} />
                          <span>{booking.date}</span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-600">
                          <Clock size={16} />
                          <span>{booking.time}</span>
                        </div>
                      </div>

                      {/* Address */}
                      <div className="flex items-start gap-2 text-sm text-gray-600">
                        <MapPin size={16} className="mt-0.5 flex-shrink-0" />
                        <span className="line-clamp-1">{booking.pickup_address}</span>
                      </div>
                    </div>

                    {/* Actions - Right Side */}
                    <div className="md:col-span-4 flex flex-col gap-2">
                      <Button
                        onClick={() => viewDetails(booking)}
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                      >
                        <Eye size={16} className="mr-2" />
                        View Details
                      </Button>
                      <a href={`tel:${booking.customer_phone}`}>
                        <Button variant="outline" className="w-full border-blue-600 text-blue-600 hover:bg-blue-50">
                          <Phone size={16} className="mr-2" />
                          Call Customer
                        </Button>
                      </a>
                      
                      {/* Status Update Buttons */}
                      {booking.status === 'pending' && (
                        <Button
                          onClick={() => updateBookingStatus(booking.id, 'confirmed')}
                          variant="outline"
                          className="w-full border-green-600 text-green-600 hover:bg-green-50"
                        >
                          <CheckCircle size={16} className="mr-2" />
                          Confirm
                        </Button>
                      )}
                      
                      {booking.status === 'confirmed' && (
                        <>
                          <Button
                            onClick={() => sendDOToDriverWhatsApp(booking)}
                            className="w-full bg-[#25D366] hover:bg-[#1ebe57] text-white"
                            data-testid={`send-do-whatsapp-${booking.id}`}
                          >
                            <MessageCircle size={16} className="mr-2" />
                            Send to Driver (WhatsApp)
                          </Button>
                          <Button
                            onClick={() => updateBookingStatus(booking.id, 'completed')}
                            variant="outline"
                            className="w-full border-blue-600 text-blue-600 hover:bg-blue-50"
                          >
                            <CheckCircle size={16} className="mr-2" />
                            Complete
                          </Button>
                        </>
                      )}
                      
                      {(booking.status === 'pending' || booking.status === 'confirmed') && (
                        <Button
                          onClick={() => updateBookingStatus(booking.id, 'cancelled')}
                          variant="outline"
                          className="w-full border-red-600 text-red-600 hover:bg-red-50"
                        >
                          <XCircle size={16} className="mr-2" />
                          Cancel
                        </Button>
                      )}
                      
                      <Button
                        onClick={() => deleteBooking(booking.id)}
                        variant="outline"
                        className="w-full border-red-600 text-red-600 hover:bg-red-50"
                      >
                        <Trash2 size={16} className="mr-2" />
                        Delete
                      </Button>
                      
                      <p className="text-xs text-gray-500 text-center mt-2">
                        Submitted: {formatDate(booking.created_at)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Booking Details Modal */}
      <Dialog open={showDetails} onOpenChange={setShowDetails}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedBooking && (
            <>
              <DialogHeader>
                <DialogTitle className="text-2xl">Booking Details</DialogTitle>
                <DialogDescription>
                  Complete information for booking ID: {selectedBooking.id.substring(0, 8)}...
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-6 mt-4">
                {/* Status */}
                <div>
                  <Label className="text-sm font-semibold text-gray-700">Status</Label>
                  <Badge className={`${getStatusColor(selectedBooking.status)} border mt-2`}>
                    {selectedBooking.status.charAt(0).toUpperCase() + selectedBooking.status.slice(1)}
                  </Badge>
                </div>

                {/* Transport Type */}
                <div>
                  <Label className="text-sm font-semibold text-gray-700">Transport Type</Label>
                  <p className="mt-1 text-gray-900">
                    {selectedBooking.vehicle_qty && parseInt(selectedBooking.vehicle_qty) > 1 ? `${selectedBooking.vehicle_qty}x ` : ''}{formatTransportType(selectedBooking.transport_type)}
                  </p>
                </div>

                {/* Company Details */}
                {selectedBooking.company_name && (
                  <div className="border-t pt-4">
                    <h4 className="font-semibold text-gray-900 mb-3">Company Details</h4>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm text-gray-600">Company Name</Label>
                        <p className="mt-1 font-medium text-gray-900">{selectedBooking.company_name}</p>
                      </div>
                      <div>
                        <Label className="text-sm text-gray-600">Company Contact</Label>
                        <p className="mt-1">
                          <a href={`tel:${selectedBooking.company_contact}`} className="text-blue-600 hover:underline">
                            {selectedBooking.company_contact}
                          </a>
                        </p>
                      </div>
                      <div>
                        <Label className="text-sm text-gray-600">Company Email</Label>
                        <p className="mt-1">
                          <a href={`mailto:${selectedBooking.company_email}`} className="text-blue-600 hover:underline">
                            {selectedBooking.company_email}
                          </a>
                        </p>
                      </div>
                      {selectedBooking.company_address && (
                        <div>
                          <Label className="text-sm text-gray-600">Company Address</Label>
                          <p className="mt-1 text-gray-900">{selectedBooking.company_address}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Booking Details (Contact Person) */}
                <div className="border-t pt-4">
                  <h4 className="font-semibold text-gray-900 mb-3">Booking Contact Person</h4>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm text-gray-600">Name</Label>
                      <p className="mt-1 font-medium text-gray-900">{selectedBooking.customer_name}</p>
                    </div>
                    <div>
                      <Label className="text-sm text-gray-600">Phone</Label>
                      <p className="mt-1">
                        <a href={`tel:${selectedBooking.customer_phone}`} className="text-blue-600 hover:underline">
                          {selectedBooking.customer_phone}
                        </a>
                      </p>
                    </div>
                    <div className="sm:col-span-2">
                      <Label className="text-sm text-gray-600">Email</Label>
                      <p className="mt-1">
                        <a href={`mailto:${selectedBooking.customer_email}`} className="text-blue-600 hover:underline">
                          {selectedBooking.customer_email}
                        </a>
                      </p>
                    </div>
                  </div>
                </div>

                {/* Delivery Details */}
                <div className="border-t pt-4">
                  <h4 className="font-semibold text-gray-900 mb-3">Delivery Details</h4>
                  <div className="space-y-4">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm text-gray-600">Pick Up Location</Label>
                        <p className="mt-1 text-gray-900">{selectedBooking.pickup_address}</p>
                      </div>
                      <div>
                        <Label className="text-sm text-gray-600">Unload Location</Label>
                        <p className="mt-1 text-gray-900">{selectedBooking.unload_address || selectedBooking.delivery_address || '-'}</p>
                      </div>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm text-gray-600">Site Contact Name</Label>
                        <p className="mt-1 font-medium text-gray-900">{selectedBooking.site_contact}</p>
                      </div>
                      <div>
                        <Label className="text-sm text-gray-600">Site Contact Phone</Label>
                        <p className="mt-1">
                          <a href={`tel:${selectedBooking.site_phone}`} className="text-blue-600 hover:underline">
                            {selectedBooking.site_phone}
                          </a>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Schedule */}
                <div className="border-t pt-4">
                  <h4 className="font-semibold text-gray-900 mb-3">Schedule</h4>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm text-gray-600">Date</Label>
                      <p className="mt-1 font-medium text-gray-900">{selectedBooking.date}</p>
                    </div>
                    <div>
                      <Label className="text-sm text-gray-600">Time</Label>
                      <p className="mt-1 font-medium text-gray-900">{selectedBooking.time}</p>
                    </div>
                  </div>
                </div>

                {/* Additional Info */}
                {selectedBooking.additional_info && (
                  <div className="border-t pt-4">
                    <Label className="text-sm font-semibold text-gray-700">Extra Details</Label>
                    <p className="mt-2 text-gray-900 bg-gray-50 p-4 rounded-lg">
                      {selectedBooking.additional_info}
                    </p>
                  </div>
                )}

                {/* Linked Documents - Only for confirmed/completed bookings */}
                {linkedDocuments && (linkedDocuments.delivery_order || linkedDocuments.invoice) && (
                  <div className="border-t pt-4">
                    <h4 className="font-semibold text-gray-900 mb-3">Linked Documents</h4>
                    <div className="grid sm:grid-cols-2 gap-4">
                      {linkedDocuments.delivery_order && (
                        <Link to={`/admin/billing/delivery-order/${linkedDocuments.delivery_order.id}`}>
                          <div className="bg-orange-50 p-4 rounded-lg border border-orange-200 hover:bg-orange-100 transition-colors cursor-pointer">
                            <div className="flex items-center gap-2 mb-2">
                              <Truck size={18} className="text-orange-600" />
                              <span className="font-semibold text-orange-800">Delivery Order</span>
                            </div>
                            <p className="text-orange-900 font-bold">{linkedDocuments.delivery_order.document_number}</p>
                            <p className="text-sm text-orange-700">${linkedDocuments.delivery_order.total_amount?.toFixed(2)}</p>
                          </div>
                        </Link>
                      )}
                      {linkedDocuments.invoice && (
                        <Link to={`/admin/billing/invoice/${linkedDocuments.invoice.id}`}>
                          <div className="bg-green-50 p-4 rounded-lg border border-green-200 hover:bg-green-100 transition-colors cursor-pointer">
                            <div className="flex items-center gap-2 mb-2">
                              <FileText size={18} className="text-green-600" />
                              <span className="font-semibold text-green-800">Invoice</span>
                            </div>
                            <p className="text-green-900 font-bold">{linkedDocuments.invoice.document_number}</p>
                            <p className="text-sm text-green-700">${linkedDocuments.invoice.total_amount?.toFixed(2)}</p>
                            <Badge className={linkedDocuments.invoice.paid ? 'bg-green-200 text-green-800 mt-1' : 'bg-red-200 text-red-800 mt-1'}>
                              {linkedDocuments.invoice.paid ? 'PAID' : 'UNPAID'}
                            </Badge>
                          </div>
                        </Link>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-2">Click to view/edit document details</p>

                    {/* Quick action: WhatsApp the DO + signing link to the driver */}
                    {linkedDocuments.delivery_order && selectedBooking.status === 'confirmed' && (
                      <Button
                        onClick={() => sendDOToDriverWhatsApp(selectedBooking)}
                        className="w-full mt-4 bg-[#25D366] hover:bg-[#1ebe57] text-white"
                        data-testid="dialog-send-do-whatsapp"
                      >
                        <MessageCircle size={16} className="mr-2" />
                        Send delivery details + signing link to driver (WhatsApp)
                      </Button>
                    )}
                  </div>
                )}

                {/* Timestamps */}
                <div className="border-t pt-4 text-sm text-gray-500">
                  <p>Created: {new Date(selectedBooking.created_at).toLocaleString('en-SG')}</p>
                  <p>Last Updated: {new Date(selectedBooking.updated_at).toLocaleString('en-SG')}</p>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
